import * as React from 'react';

import type {
  FilterState,
  InstructorsFilterContextValue,
  InstructorsFilterProviderProps,
} from './InstructorsFilterContext.types';

import { arePanelFiltersEqual } from './InstructorsFilterProvider.utils';

const initialFilterState: FilterState = {
  searchText: '',
  disciplines: [],
  programs: [],
  locations: [],
};

const InstructorsFilterContext = React.createContext<
  InstructorsFilterContextValue | undefined
>(undefined);

const InstructorsFilterProvider = React.memo(
  ({
    children,
  }: Readonly<InstructorsFilterProviderProps>): React.ReactElement => {
    const [filters, setFiltersState] =
      React.useState<FilterState>(initialFilterState);

    const isFiltersApplied = React.useMemo(
      () => !arePanelFiltersEqual(filters, initialFilterState),
      [filters]
    );

    const isSearchApplied = React.useMemo(
      () => filters.searchText !== '',
      [filters.searchText]
    );

    const setFilters = React.useCallback(
      (partial: Partial<FilterState>): void => {
        setFiltersState(prev => ({ ...prev, ...partial }));
      },
      []
    );

    const clearFilters = React.useCallback((): void => {
      setFiltersState(initialFilterState);
    }, []);

    const contextValue: InstructorsFilterContextValue = React.useMemo(
      () => ({ filters, isFiltersApplied, isSearchApplied, setFilters, clearFilters }),
      [filters, isFiltersApplied, isSearchApplied, setFilters, clearFilters]
    );

    return (
      <InstructorsFilterContext value={contextValue}>
        {children}
      </InstructorsFilterContext>
    );
  }
);

InstructorsFilterProvider.displayName = 'InstructorsFilterProvider';

export { InstructorsFilterProvider, InstructorsFilterContext };
